import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Paper,
  Typography,
  Grid,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Backdrop,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  Save,
  ArrowBack,
  Title,
  Message,
  Subtitles,
  ExpandMore,
  TextFields,
  Image,
  VideoFile,
  InsertDriveFile,
  Code,
  PlayArrow,
  Add,
  Delete,
  SmartButton,
  Link,
  Phone,
  Build,
  Campaign,
  Security,
  CheckCircle,
  Visibility,
  VisibilityOff,
  CloudUpload,
  NavigateNext,
  ChevronLeft,
  ContentCopy,
  PhoneAndroid,
  AccessTime,
} from '@mui/icons-material';
import useNotification from '../common/Notification';
import axiosInstance from '../../api/axiosInstance';
import base_url from '../../constants';
import { useLocation, useNavigate } from 'react-router-dom';
import WHATSAPP_MEDIA_EXAMPLE_URLS from '../../constants/whatsappMediaExamples';
import TemplateCreationSteppers, { AUTH_TEMPLATE_STEPS } from './template/TemplateCreationSteppers';
import TemplatePreview from './template/TemplatePreview';
import { WB_SX, WB_UI } from '../../ui/ux/uiTokens';

const CreateEditWhatsappBusinessTemplate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const templateDataFromRoute = location?.state?.template || {};
  // template.id from Templates.js is the WhatsApp API template_id (e.g., "1386379053155422")
  const templateIdFromAPI = templateDataFromRoute.id || templateDataFromRoute.template_id || new URLSearchParams(location.search).get('id');
  const isEditMode = !!templateIdFromAPI;

  // Always fetch from WhatsApp API when in edit mode to get complete template details with all components
  // The template list might not include all component details (especially buttons)
  const needsFetch = useMemo(() => isEditMode && templateIdFromAPI, [isEditMode, templateIdFromAPI]);

  const [loading, setLoading] = useState(needsFetch);
  const hasFetchedRef = useRef(false);
  const [templateData, setTemplateData] = useState(templateDataFromRoute);
  const [selectedTemplateId, setSelectedTemplateId] = useState(templateIdFromAPI);
  const [templateName, setTemplateName] = useState(templateDataFromRoute.name || '');
  const [language, setLanguage] = useState(templateDataFromRoute.language || 'en_US');
  const [category, setCategory] = useState(templateDataFromRoute.category || 'UTILITY');
  
  // Template components
  const getInitialHeaderType = () => {
    return templateDataFromRoute.components?.find(c => c.type === 'HEADER')?.format || 'TEXT';
  };
  
  const getInitialMediaPreview = (headerType) => {
    if (headerType === 'IMAGE') {
      return WHATSAPP_MEDIA_EXAMPLE_URLS.IMAGE;
    } else if (headerType === 'VIDEO') {
      return WHATSAPP_MEDIA_EXAMPLE_URLS.VIDEO;
    } else if (headerType === 'DOCUMENT') {
      return WHATSAPP_MEDIA_EXAMPLE_URLS.DOCUMENT;
    }
    return null;
  };
  
  // File mapping for public/files folder
  const MEDIA_FILES = {
    IMAGE: '/files/image1.jpg',
    VIDEO: '/files/video.mp4',
    DOCUMENT: '/files/dummy.pdf',
  };

  const initialHeaderType = getInitialHeaderType();
  const [header, setHeader] = useState({
    type: initialHeaderType,
    text: templateDataFromRoute.components?.find(c => c.type === 'HEADER')?.text || '',
    examples: [], // Array of example values for header parameters
    mediaPreview: getInitialMediaPreview(initialHeaderType), // URL for preview (uses example URLs for media types)
    mediaId: null, // Media handle from WhatsApp API (used in header_handle)
    mediaSource: 'system', // 'system' | 'custom' - system uses MEDIA_FILES, custom uses default_media_link API
    defaultMediaLink: null, // Cloudflare R2 URL from default_media_link API (sent in submit body)
  });
  
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [body, setBody] = useState({
    text: templateDataFromRoute.components?.find(c => c.type === 'BODY')?.text || '',
    examples: [], // Array of example values for body parameters
  });
  const [footer, setFooter] = useState({
    text: templateDataFromRoute.components?.find(c => c.type === 'FOOTER')?.text || '',
  });
  const [buttons, setButtons] = useState(
    templateDataFromRoute.components?.find(c => c.type === 'BUTTONS')?.buttons || []
  );
  const [addSecurityRecommendation, setAddSecurityRecommendation] = useState(true);
  const [codeExpirationMinutes, setCodeExpirationMinutes] = useState('');
  const [messageSendTtlSeconds, setMessageSendTtlSeconds] = useState('');
  const [otpType, setOtpType] = useState('COPY_CODE');
  const [supportedApp, setSupportedApp] = useState({ package_name: '', signature_hash: '' });
  const isAuthTemplate = category === 'AUTHENTICATION';
  const lastStep = isAuthTemplate ? 3 : 4;
  const authPreviewBody = addSecurityRecommendation
    ? '{{1}} is your verification code. For your security, do not share this code.'
    : '{{1}} is your verification code.';
  const authPreviewFooter = codeExpirationMinutes
    ? `This code expires in ${codeExpirationMinutes} minute${Number(codeExpirationMinutes) === 1 ? '' : 's'}.`
    : '';
  const authPreviewButtons = [{
    type: 'OTP',
    otp_type: otpType,
    text: otpType === 'ONE_TAP' ? 'Autofill' : 'Copy Code',
  }];

  const [selection, setSelection] = useState({ start: 0, end: 0, field: 'body' });
  const [cursorPosition, setCursorPosition] = useState({ header: 0, body: 0, footer: 0 });
  const textFieldRef = useRef(null);
  const headerFieldRef = useRef(null);
  const bodyFieldRef = useRef(null);
  const footerFieldRef = useRef(null);
  const customMediaInputRef = useRef(null);
  const notify = useNotification();

  // Handle media upload - automatically upload file from public/files based on type
  const handleMediaUpload = async (typeOverride = null) => {
    const mediaTypeValue = typeOverride || header.type;
    
    if (mediaTypeValue === "TEXT" || !mediaTypeValue) {
      return;
    }

    // If already uploaded and no type override, don't upload again
    // If type override is provided, we're switching types so allow upload
    if (!typeOverride && header.mediaId) {
      return;
    }

    // Get file path from MEDIA_FILES mapping
    const filePath = MEDIA_FILES[mediaTypeValue];
    if (!filePath) {
      notify(`No file found for ${mediaTypeValue} type`, "error");
      return;
    }

    // Map header type to API type (IMAGE -> image, VIDEO -> video, DOCUMENT -> document)
    const mediaType = mediaTypeValue.toLowerCase();
    
    setUploadingMedia(true);
    try {
      // Fetch file from public folder and send as binary
      const fileUrl = filePath; // e.g., "/files/image1.jpg"
      const response = await fetch(fileUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${fileUrl}`);
      }
      
      const blob = await response.blob();
      const file = new File([blob], filePath.split('/').pop(), { type: blob.type });
      
      // Create FormData with file (binary)
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", mediaType);

      // Backend will handle all config (app_id, access_token, phone_number_id) from database
      const uploadResponse = await axiosInstance.post(
        `${base_url}/whatsapp-business/media/create-template/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (uploadResponse.data?.h || uploadResponse.data?.id) {
        const handle = uploadResponse.data.h || uploadResponse.data.id;
        setHeader(prev => ({ ...prev, mediaId: handle }));
        // Don't show success notification for automatic uploads to avoid spam
      } else {
        notify("Failed to get media handle from upload response", "error");
      }
    } catch (error) {
      console.error("Error uploading media:", error);
      notify(
        error.response?.data?.error || "Failed to upload media. Please try again.",
        "error"
      );
    } finally {
      setUploadingMedia(false);
    }
  };

  // Handle custom/default media upload - uses default_media_link API
  const handleDefaultMediaUpload = async (file) => {
    if (!file || header.type === "TEXT") return;

    const mediaType = header.type.toLowerCase(); // IMAGE -> image, etc.
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", mediaType);

    setUploadingMedia(true);
    try {
      const response = await axiosInstance.post(
        `${base_url}/whatsapp-business/upload/template/default_media_link`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const handle = response.data?.h || response.data?.id;
      const mediaLink = response.data?.media_link || response.data?.default_media_link;

      if (handle) {
        setHeader((prev) => ({
          ...prev,
          mediaId: handle,
          defaultMediaLink: mediaLink || null,
          mediaPreview: mediaLink ? mediaLink : prev.mediaPreview,
        }));
      } else {
        notify("Failed to get media handle from upload response", "error");
      }
    } catch (error) {
      console.error("Error uploading default media:", error);
      notify(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to upload media. Please try again.",
        "error"
      );
    } finally {
      setUploadingMedia(false);
    }
  };

  // Trigger file picker for custom media
  const handleCustomMediaClick = () => {
    customMediaInputRef.current?.click();
  };

  const handleCustomMediaFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleDefaultMediaUpload(file);
    }
    e.target.value = "";
  };

  // Fetch template data from WhatsApp API when in edit mode
  useEffect(() => {
    // Prevent multiple fetches - only fetch once per templateIdFromAPI
    if (!templateIdFromAPI || !isEditMode) {
      setLoading(false);
      return;
    }
    
    // Reset fetch flag when templateId changes
    if (hasFetchedRef.current && hasFetchedRef.current !== templateIdFromAPI) {
      hasFetchedRef.current = false;
    }
    
    // Skip if already fetched for this template
    if (hasFetchedRef.current === templateIdFromAPI) {
      return;
    }
    
    let isMounted = true;
    hasFetchedRef.current = templateIdFromAPI;
    
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        // Fetch from WhatsApp API using template_id - get full template details
        const response = await axiosInstance.get(
          `${base_url}/whatsapp-business/template/${templateIdFromAPI}/details`,
          {
            params: {
              fields: 'id,name,language,category,status,components,previous_category,parameter_format'
            }
          }
        );
        
        if (!isMounted) return;
        
        const fetchedTemplate = response.data;
        setTemplateData(fetchedTemplate);
        
        // Update form fields with fetched data
        setTemplateName(fetchedTemplate.name || '');
        setLanguage(fetchedTemplate.language || 'en_US');
        setCategory(fetchedTemplate.category || 'UTILITY');

        const bodyComponent = fetchedTemplate.components?.find(c => c.type === 'BODY');
        const footerComponent = fetchedTemplate.components?.find(c => c.type === 'FOOTER');
        const buttonsComponent = fetchedTemplate.components?.find(c => c.type === 'BUTTONS');

        if (fetchedTemplate.category === 'AUTHENTICATION') {
          setAddSecurityRecommendation(bodyComponent?.add_security_recommendation !== false);
          if (footerComponent?.code_expiration_minutes != null) {
            setCodeExpirationMinutes(String(footerComponent.code_expiration_minutes));
          }
          if (fetchedTemplate.message_send_ttl_seconds != null) {
            setMessageSendTtlSeconds(String(fetchedTemplate.message_send_ttl_seconds));
          }
          const otpBtn = (buttonsComponent?.buttons || []).find(
            (b) => b.type === 'OTP' || b.otp_type === 'COPY_CODE' || b.otp_type === 'ONE_TAP'
          );
          if (otpBtn) {
            setOtpType(otpBtn.otp_type || (otpBtn.type === 'ONE_TAP' ? 'ONE_TAP' : 'COPY_CODE'));
            const app = otpBtn.supported_apps?.[0];
            if (app) {
              setSupportedApp({
                package_name: app.package_name || '',
                signature_hash: app.signature_hash || '',
              });
            }
          }
        }
        
        // Update header
        const headerComponent = fetchedTemplate.components?.find(c => c.type === 'HEADER');
        if (headerComponent) {
          const headerType = headerComponent.format || 'TEXT';
          // For media types, extract media_id from example.header_handle if available
          let mediaId = null;
          let mediaPreview = null;
          
          if (headerType === 'IMAGE' || headerType === 'VIDEO' || headerType === 'DOCUMENT') {
            // Check if example.header_handle exists and contains media_id
            if (headerComponent.example?.header_handle && headerComponent.example.header_handle[0]) {
              const handleValue = headerComponent.example.header_handle[0];
              // If it's a numeric string, it's a media_id, otherwise it's a URL
              if (/^\d+$/.test(handleValue)) {
                mediaId = handleValue;
                // For preview, use example URL (we can't fetch the actual media without additional API call)
                if (headerType === 'IMAGE') {
                  mediaPreview = WHATSAPP_MEDIA_EXAMPLE_URLS.IMAGE;
                } else if (headerType === 'VIDEO') {
                  mediaPreview = WHATSAPP_MEDIA_EXAMPLE_URLS.VIDEO;
                } else if (headerType === 'DOCUMENT') {
                  mediaPreview = WHATSAPP_MEDIA_EXAMPLE_URLS.DOCUMENT;
                }
              } else {
                // It's a URL (old format)
                mediaPreview = handleValue;
              }
            } else {
              // No example, use default preview URL
              if (headerType === 'IMAGE') {
                mediaPreview = WHATSAPP_MEDIA_EXAMPLE_URLS.IMAGE;
              } else if (headerType === 'VIDEO') {
                mediaPreview = WHATSAPP_MEDIA_EXAMPLE_URLS.VIDEO;
              } else if (headerType === 'DOCUMENT') {
                mediaPreview = WHATSAPP_MEDIA_EXAMPLE_URLS.DOCUMENT;
              }
            }
          }
          
          setHeader({
            type: headerType,
            text: headerComponent.text || '',
            examples: [],
            mediaPreview: mediaPreview,
            mediaId: mediaId,
            mediaSource: 'system',
            defaultMediaLink: null,
          });
        }
        
        // Update body
        if (bodyComponent) {
          const bodyData = {
            text: bodyComponent.text || '',
            examples: [],
          };
          
          // Extract example values if available
          if (bodyComponent.example && bodyComponent.example.body_text && bodyComponent.example.body_text[0]) {
            bodyData.examples = bodyComponent.example.body_text[0];
          }
          
          setBody(bodyData);
        }
        
        // Update footer
        if (footerComponent) {
          setFooter({
            text: footerComponent.text || '',
          });
        }
        
        // Update buttons
        if (buttonsComponent && buttonsComponent.buttons) {
          setButtons(buttonsComponent.buttons);
        }
        
        setLoading(false);
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching template:", error);
        notify(
          error.response?.data?.error || "Failed to load template",
          "error"
        );
        navigate('/whatsapp-business/templates');
        setLoading(false);
      }
    };
    
    fetchTemplate();
    
    return () => {
      isMounted = false;
    };
  }, [templateIdFromAPI, isEditMode]); // Only depend on templateIdFromAPI and isEditMode

  // Extract parameter numbers from text (e.g., {{1}}, {{2}})
  const extractParameters = (text) => {
    const matches = text.match(/\{\{(\d+)\}\}/g) || [];
    const paramNumbers = matches.map(match => parseInt(match.replace(/\{\{|\}\}/g, '')));
    return [...new Set(paramNumbers)].sort((a, b) => a - b);
  };

  // Validate parameters not at start or end
  const validateParameterPosition = (text, field) => {
    const trimmedText = text.trim();
    if (trimmedText.startsWith('{{') || trimmedText.endsWith('}}')) {
      notify(`Parameters cannot be at the start or end of ${field} text`, "error");
      return false;
    }
    return true;
  };

  // Validate template name (no uppercase, max 512 chars)
  const validateTemplateName = (name) => {
    if (name !== name.toLowerCase()) {
      notify("Template name cannot contain uppercase letters", "error");
      return false;
    }
    if (name.length > 512) {
      notify("Template name cannot exceed 512 characters", "error");
      return false;
    }
    return true;
  };

  const handleContentChange = (e, field) => {
    let value = e.target.value;
    
    // Character limits
    if (field === 'body' && value.length > 1024) {
      notify("Body text cannot exceed 1024 characters", "error");
      return;
    }
    if (field === 'header' && value.length > 60) {
      notify("Header text cannot exceed 60 characters", "error");
      return;
    }
    if (field === 'footer' && value.length > 60) {
      notify("Footer text cannot exceed 60 characters", "error");
      return;
    }
    
    // Update cursor position
    const cursorPos = e.target.selectionStart;
    if (field === 'body') {
      setCursorPosition(prev => ({ ...prev, body: cursorPos }));
    } else if (field === 'header') {
      setCursorPosition(prev => ({ ...prev, header: cursorPos }));
    }
    
    if (field === 'body') {
      const params = extractParameters(value);
      const currentExamples = body.examples || [];
      const newExamples = [...currentExamples];
      // Resize examples array to match parameter count
      while (newExamples.length < params.length) {
        newExamples.push('');
      }
      setBody({ ...body, text: value, examples: newExamples.slice(0, params.length) });
    } else if (field === 'header') {
      const params = extractParameters(value);
      const currentExamples = header.examples || [];
      const newExamples = [...currentExamples];
      // Resize examples array to match parameter count
      while (newExamples.length < params.length) {
        newExamples.push('');
      }
      setHeader({ ...header, text: value, examples: newExamples.slice(0, params.length) });
    } else if (field === 'footer') {
      setFooter({ ...footer, text: value });
    }
    
    setSelection({
      start: e.target.selectionStart,
      end: e.target.selectionEnd,
      field: field,
    });
  };

  const handleTemplateNameChange = (e) => {
    let value = e.target.value;
    // Convert to lowercase
    value = value.toLowerCase();
    // Limit to 512 characters
    if (value.length > 512) {
      notify("Template name cannot exceed 512 characters", "error");
      return;
    }
    setTemplateName(value);
  };

  const handleTextSelection = (e, field) => {
    const cursorPos = e.target.selectionStart;
    // Update cursor position
    if (field === 'body') {
      setCursorPosition(prev => ({ ...prev, body: cursorPos }));
    } else if (field === 'header') {
      setCursorPosition(prev => ({ ...prev, header: cursorPos }));
    }
    
    setSelection({
      start: e.target.selectionStart,
      end: e.target.selectionEnd,
      field: field,
    });
  };

  const getCurrentFieldRef = () => {
    if (selection.field === 'header') return headerFieldRef;
    if (selection.field === 'body') return bodyFieldRef;
    if (selection.field === 'footer') return footerFieldRef;
    return bodyFieldRef;
  };

  const getCurrentFieldValue = () => {
    if (selection.field === 'header') return header.text;
    if (selection.field === 'body') return body.text;
    if (selection.field === 'footer') return footer.text;
    return body.text;
  };

  const setCurrentFieldValue = (value) => {
    if (selection.field === 'header') {
      setHeader({ ...header, text: value });
    } else if (selection.field === 'body') {
      setBody({ ...body, text: value });
    } else if (selection.field === 'footer') {
      setFooter({ ...footer, text: value });
    }
  };

  const handleFormatToggle = (format) => {
    const fieldRef = getCurrentFieldRef().current;
    if (!fieldRef) return;

    const start = fieldRef.selectionStart;
    const end = fieldRef.selectionEnd;
    
    if (start === end) {
      return; // No text selected
    }

    applyFormattingToSelection(format, start, end);
  };

  const applyFormattingToSelection = (format, start, end) => {
    const formatMap = {
      bold: { prefix: '*', suffix: '*' },
      italic: { prefix: '_', suffix: '_' },
      strikethrough: { prefix: '~', suffix: '~' },
    };

    const formatting = formatMap[format];
    if (!formatting) return;

    const currentValue = getCurrentFieldValue();
    const selectedText = currentValue.substring(start, end);
    const beforeText = currentValue.substring(0, start);
    const afterText = currentValue.substring(end);
    
    const newContent = `${beforeText}${formatting.prefix}${selectedText}${formatting.suffix}${afterText}`;
    setCurrentFieldValue(newContent);
    
    // Set cursor position after the formatted text
    setTimeout(() => {
      const fieldRef = getCurrentFieldRef().current;
      if (fieldRef) {
        const newEnd = end + formatting.prefix.length + formatting.suffix.length;
        fieldRef.focus();
        fieldRef.setSelectionRange(newEnd, newEnd);
      }
    }, 0);
  };

  const insertParameter = (field, paramNumber) => {
    const param = `{{${paramNumber}}}`;
    let currentText = '';
    let cursorPos = 0;
    let fieldRef = null;
    
    if (field === "body") {
      currentText = body.text || '';
      cursorPos = cursorPosition?.body ?? currentText.length;
      fieldRef = bodyFieldRef;
    } else if (field === "header") {
      currentText = header.text || '';
      cursorPos = cursorPosition?.header ?? currentText.length;
      fieldRef = headerFieldRef;
    } else {
      return;
    }
    
    // Ensure cursorPos is within valid range
    if (cursorPos < 0) cursorPos = 0;
    if (cursorPos > currentText.length) cursorPos = currentText.length;
    
    // Insert parameter at cursor position
    const beforeCursor = currentText.substring(0, cursorPos);
    const afterCursor = currentText.substring(cursorPos);
    const newText = beforeCursor + param + afterCursor;
    
    // Validate parameter position
    if (!validateParameterPosition(newText, field)) {
      return;
    }
    
    // Update the text
    if (field === "body") {
      setBody({ ...body, text: newText });
    } else if (field === "header") {
      setHeader({ ...header, text: newText });
    }
    
    // Update cursor position after insertion
    const newCursorPos = cursorPos + param.length;
    setTimeout(() => {
      if (fieldRef && fieldRef.current) {
        fieldRef.current.focus();
        fieldRef.current.setSelectionRange(newCursorPos, newCursorPos);
        // Update cursor position state
        if (field === "body") {
          setCursorPosition(prev => ({ ...prev, body: newCursorPos }));
        } else if (field === "header") {
          setCursorPosition(prev => ({ ...prev, header: newCursorPos }));
        }
      }
    }, 0);
  };

  // Insert next parameter automatically (increments based on existing parameters)
  const insertNextParameter = (field) => {
    let currentText = '';
    
    if (field === "body") {
      currentText = body.text || '';
    } else if (field === "header") {
      currentText = header.text || '';
    } else {
      return;
    }
    
    // Extract all existing parameter numbers
    const existingParams = extractParameters(currentText);
    
    // Find the next parameter number (highest + 1, or 1 if no parameters exist)
    const nextParamNumber = existingParams.length > 0 
      ? Math.max(...existingParams) + 1 
      : 1;
    
    // Insert the parameter
    insertParameter(field, nextParamNumber);
  };

  const parseWhatsappFormatting = (text) => {
    if (!text) return '';
    
    // Replace newlines with <br> tags first
    let html = text.replace(/\n/g, '<br>');
    
    // Bold: *text*
    html = html.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
    
    // Italic: _text_
    html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
    
    // Strikethrough: ~text~
    html = html.replace(/~([^~]+)~/g, '<del>$1</del>');
    
    // Parameters: {{1}}, {{2}}, etc.
    html = html.replace(/\{\{(\d+)\}\}/g, '<span style="background-color: #fff3cd; padding: 2px 4px; border-radius: 3px; font-weight: bold;">{{$1}}</span>');
    
    return html;
  };

  const buildComponents = () => {
    if (isAuthTemplate) {
      const components = [
        {
          type: "BODY",
          add_security_recommendation: Boolean(addSecurityRecommendation),
        },
      ];
      if (codeExpirationMinutes) {
        components.push({
          type: "FOOTER",
          code_expiration_minutes: Number(codeExpirationMinutes),
        });
      }
      const otpButton = {
        type: "OTP",
        otp_type: otpType,
      };
      if (otpType === "ONE_TAP") {
        otpButton.supported_apps = [
          {
            package_name: supportedApp.package_name.trim(),
            signature_hash: supportedApp.signature_hash.trim(),
          },
        ];
      }
      components.push({
        type: "BUTTONS",
        buttons: [otpButton],
      });
      return components;
    }

    const components = [];

    // Header component
    if (header.type === "TEXT" && header.text.trim()) {
      const headerComponent = {
        type: "HEADER",
        format: "TEXT",
        text: header.text,
      };

      // Add example parameters if header has parameters and examples are provided
      const headerParams = extractParameters(header.text);
      if (headerParams.length > 0 && header.examples && header.examples.length > 0) {
        // Filter examples to match parameter count and remove empty values
        // Format: header_text: ["string1", "string2", ...] for multiple positional parameters
        const exampleValues = header.examples
          .slice(0, headerParams.length)
          .filter(val => val && val.trim())
          .map(val => val.trim());
        if (exampleValues.length > 0) {
          headerComponent.example = {
            header_text: exampleValues  // Direct array of strings, not wrapped in another array
          };
        }
      }

      components.push(headerComponent);
    } 
    
    // Media header types (IMAGE, VIDEO, DOCUMENT) - ALWAYS include example field with handle
    if (header.type === "IMAGE" || header.type === "VIDEO" || header.type === "DOCUMENT") {
      // For media types, use handle from WhatsApp API upload
      // Format: { type: "HEADER", format: "IMAGE", example: { header_handle: ["handle"] } }
      // Always include example field - WhatsApp API requires it for media headers
      
      if (!header.mediaId) {
        // This should not happen if validation in handleSubmit worked correctly
        // But if it does, return empty components to prevent invalid template creation
        console.error(`Media handle missing for ${header.type} header type`);
        return components; // Return early if no handle
      }
      
      const headerComponent = {
        type: "HEADER",
        format: header.type,
        example: {
          header_handle: [header.mediaId]  // Array with single media_id string - matches format: ["1407680676729941"]
        }
      };
      
      console.log(`Adding media header component:`, JSON.stringify(headerComponent, null, 2));
      components.push(headerComponent);
    }

    // Body component (required)
    if (body.text.trim()) {
      const bodyComponent = {
        type: "BODY",
        text: body.text,
      };

      // Add example parameters if body has parameters and examples are provided
      const bodyParams = extractParameters(body.text);
      if (bodyParams.length > 0 && body.examples && body.examples.length > 0) {
        // Filter examples to match parameter count and remove empty values
        // Format: body_text: [["string1", "string2", ...]] for multiple positional parameters (array of arrays)
        // Ensure body.examples is a flat array (not nested)
        const flatExamples = Array.isArray(body.examples[0]) ? body.examples[0] : body.examples;
        const exampleValues = flatExamples
          .slice(0, bodyParams.length)
          .filter(val => val && val.trim && typeof val.trim === 'function' ? val.trim() : String(val).trim())
          .map(val => (val && val.trim && typeof val.trim === 'function' ? val.trim() : String(val).trim()));
        if (exampleValues.length > 0) {
          bodyComponent.example = {
            body_text: [exampleValues]  // Array of arrays: [["string1", "string2", ...]]
          };
        }
      }

      components.push(bodyComponent);
    }

    // Footer component
    if (footer.text.trim()) {
      components.push({
        type: "FOOTER",
        text: footer.text,
      });
    }

    // Buttons component - filter out empty buttons
    if (buttons && buttons.length > 0) {
      const validButtons = buttons.filter(button => 
        button.text && button.text.trim() && 
        (button.type !== 'URL' || (button.url && button.url.trim())) &&
        (button.type !== 'PHONE_NUMBER' || (button.phone_number && button.phone_number.trim()))
      );
      
      if (validButtons.length > 0) {
        components.push({
          type: "BUTTONS",
          buttons: validButtons.map(button => {
            const buttonData = {
              type: button.type || 'URL',
              text: button.text.trim(),
            };
            
            // Add URL only for URL type buttons
            if (button.type === 'URL' && button.url) {
              buttonData.url = button.url.trim();
            }
            
            // Add phone_number for PHONE_NUMBER type buttons
            if (button.type === 'PHONE_NUMBER' && button.phone_number) {
              buttonData.phone_number = button.phone_number.trim();
            }
            
            return buttonData;
          }),
        });
      }
    }

    return components;
  };

  const handleSubmit = async () => {
    if (!templateName.trim()) {
      notify("Please enter template name", "error");
      return;
    }
    if (!validateTemplateName(templateName)) {
      return;
    }
    if (isAuthTemplate) {
      if (codeExpirationMinutes) {
        const mins = Number(codeExpirationMinutes);
        if (!Number.isInteger(mins) || mins < 1 || mins > 90) {
          notify("Code expiration must be between 1 and 90 minutes", "error");
          return;
        }
      }
      if (messageSendTtlSeconds) {
        const ttl = Number(messageSendTtlSeconds);
        if (!Number.isInteger(ttl) || ttl < 30) {
          notify("Message TTL must be an integer of at least 30 seconds", "error");
          return;
        }
      }
      if (otpType === "ONE_TAP") {
        if (!supportedApp.package_name.trim() || !supportedApp.signature_hash.trim()) {
          notify("ONE_TAP requires Android package name and signature hash", "error");
          return;
        }
      }
    } else {
      if (!body.text.trim()) {
        notify("Body text is required", "error");
        return;
      }
      if (body.text.length > 1024) {
        notify("Body text cannot exceed 1024 characters", "error");
        return;
      }
      if (header.text.length > 60) {
        notify("Header text cannot exceed 60 characters", "error");
        return;
      }
      if (footer.text.length > 60) {
        notify("Footer text cannot exceed 60 characters", "error");
        return;
      }
      if (body.text.trim() && !validateParameterPosition(body.text, 'body')) {
        return;
      }
      if (header.text.trim() && !validateParameterPosition(header.text, 'header')) {
        return;
      }
    }
    
    // Validate media header has media_id before building components
    if (!isAuthTemplate && (header.type === "IMAGE" || header.type === "VIDEO" || header.type === "DOCUMENT")) {
      if (!header.mediaId) {
        // If upload is in progress, wait for it
        if (uploadingMedia) {
          notify(`Please wait for media upload to complete for ${header.type} header type.`, "info");
          return;
        }
        // If no media ID and not uploading, trigger upload (system only - custom requires file pick)
        if (header.mediaSource === 'system') {
          notify(`Uploading media for ${header.type} header type...`, "info");
          await handleMediaUpload();
        } else {
          notify(`Please upload your custom media for ${header.type} header type.`, "error");
          return;
        }
        // Check again after upload attempt
        if (!header.mediaId) {
          notify(`Failed to upload media for ${header.type} header type. Please try again.`, "error");
          return;
        }
      }
    }

    try {
      const components = buildComponents();
      
      // Validate that media headers have example field
      const mediaHeaders = components.filter(c => 
        c.type === "HEADER" && 
        (c.format === "IMAGE" || c.format === "VIDEO" || c.format === "DOCUMENT")
      );
      
      for (const headerComp of mediaHeaders) {
        if (!headerComp.example || !headerComp.example.header_handle || !Array.isArray(headerComp.example.header_handle) || headerComp.example.header_handle.length === 0) {
          notify(`Missing example URL for ${headerComp.format} header type. Please ensure media preview is set.`, "error");
          return;
        }
      }
      
      // Debug: Log the components structure to verify format
      console.log("Template components being sent:", JSON.stringify(components, null, 2));
      
      // Build request body - include default_media_link when using custom/default media
      const requestBody = {
        name: templateName,
        language: language,
        category: category,
        components: components,
        save_to_db: true,
      };
      if (header.defaultMediaLink) {
        requestBody.default_media_link = header.defaultMediaLink;
      }
      if (isAuthTemplate && messageSendTtlSeconds) {
        requestBody.message_send_ttl_seconds = Number(messageSendTtlSeconds);
      }

      // Create template (WhatsApp API automatically creates a new version if name+language already exists)
      await axiosInstance.post(
        `${base_url}/whatsapp-business/templates`,
        requestBody
      );

      if (isEditMode) {
        notify("Template updated successfully! (New version created)", "success");
      } else {
        notify("Template created successfully!", "success");
      }
      navigate('/whatsapp-business/templates');
    } catch (error) {
      console.error("Error saving template:", error);
      
      // Parse WhatsApp Business API error format
      let errorMessage = "Failed to save template";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check for user-friendly error message
        if (errorData.data?.error?.error_user_msg) {
          errorMessage = errorData.data.error.error_user_msg;
          // Add title if available
          if (errorData.data.error.error_user_title) {
            errorMessage = `${errorData.data.error.error_user_title}: ${errorMessage}`;
          }
        } 
        // Check for nested error message
        else if (errorData.data?.error?.message) {
          errorMessage = errorData.data.error.message;
        }
        // Check for direct error message
        else if (errorData.error) {
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          } else if (errorData.error.message) {
            errorMessage = errorData.error.message;
          } else if (errorData.error.error_user_msg) {
            errorMessage = errorData.error.error_user_msg;
          }
        }
        // Check for message field
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      notify(errorMessage, "error");
    }
  };

  const hasSelection = selection.start !== selection.end;

  // Add state for delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(true);
  
  // Stepper state
  const [currentStep, setCurrentStep] = useState(0);

  // Stepper navigation
  const handleNext = () => {
    if (currentStep === 0) {
      if (!templateName.trim()) {
        notify("Please enter template name", "error");
        return;
      }
      if (!validateTemplateName(templateName)) {
        return;
      }
      if (!category) {
        notify("Please select a category", "error");
        return;
      }
    } else if (!isAuthTemplate && currentStep === 2) {
      if (!body.text.trim()) {
        notify("Body text is required", "error");
        return;
      }
    } else if (isAuthTemplate && currentStep === lastStep) {
      handleSubmit();
      return;
    } else if (!isAuthTemplate && currentStep === 4) {
      handleSubmit();
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  // Update handleDeleteTemplate function
  const handleDeleteTemplate = async () => {
    if (!templateIdFromAPI || !templateName) return;

    try {
      setLoading(true);
      await axiosInstance.delete(
        `${base_url}/whatsapp-business/template`,
        {
          params: {
            hsm_id: templateIdFromAPI,
            name: templateName,
            delete_from_db: true,
          },
        }
      );

      notify("Template deleted successfully!", "success");
      navigate('/whatsapp-business/templates');
    } catch (error) {
      console.error("Error deleting template:", error);
      notify(
        error.response?.data?.error || "Failed to delete template",
        "error"
      );
    } finally {
      setLoading(false);
      setDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, minHeight: '100vh', backgroundColor: '#f5f5f5', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 3 }, minHeight: '100vh', backgroundColor: '#f5f5f5', position: 'relative' }}>
      {/* Full-screen loader for media upload - shows during both API calls in backend */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }}
        open={uploadingMedia}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Uploading Media to WhatsApp...
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.8, textAlign: 'center', maxWidth: 400 }}>
          Creating upload session and uploading file. This may take a few moments...
        </Typography>
      </Backdrop>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <IconButton
          onClick={() => navigate('/whatsapp-business/templates')}
          size="small"
          sx={{
            border: `1px solid ${WB_UI.color.border}`,
            backgroundColor: '#fff',
            borderRadius: 2,
          }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant='h5' sx={{ ...WB_SX.pageTitle, mb: 0.25, color: 'text.primary' }}>
            {isEditMode ? 'Edit' : 'Create'} WhatsApp Business Template
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isAuthTemplate ? 'Authentication · OTP / verification code' : 'Utility, marketing, or authentication'}
          </Typography>
        </Box>
        {isAuthTemplate && (
          <Chip label="Authentication" size="small" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
        )}
        <Tooltip title={previewVisible ? "Hide Preview" : "Show Preview"}>
          <IconButton
            onClick={() => setPreviewVisible(!previewVisible)}
            size="small"
            sx={{ border: `1px solid ${WB_UI.color.border}`, backgroundColor: '#fff', borderRadius: 2 }}
          >
            {previewVisible ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </Tooltip>
        {isEditMode && (
          <Tooltip title="Delete Template">
            <IconButton
              onClick={() => setDeleteDialog(true)}
              color="error"
              size="small"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Stepper */}
      <Box sx={{ width: "100%", mb: 4 }}>
        <TemplateCreationSteppers
          currentStep={currentStep}
          steps={isAuthTemplate ? AUTH_TEMPLATE_STEPS : undefined}
          accent={isAuthTemplate ? '#2e7d32' : '#111827'}
        />
      </Box>
      
      <Grid container spacing={3} alignItems="flex-start">
        {/* Main Form Content */}
        <Grid item xs={12} md={previewVisible ? 7 : 12}>
          <Paper elevation={0} sx={{ ...WB_SX.card, p: { xs: 2, sm: 3 }, minHeight: 520, backgroundColor: WB_UI.color.cardBgStrong }}>
          {/* Step 0: Basic Info */}
          {currentStep === 0 && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ mb: 0.75, ...WB_SX.sectionTitle, color: 'text.primary' }}>
                Basic Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Enter the template name, select language, and choose a category for your WhatsApp template.
              </Typography>
            
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Template Name"
                    value={templateName}
                    onChange={handleTemplateNameChange}
                    placeholder="e.g., welcome_message (lowercase only, max 512 chars)"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Title />
                        </InputAdornment>
                      ),
                    }}
                    helperText={`${templateName.length}/512 characters (lowercase only)`}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Language</InputLabel>
                    <Select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      label="Language"
                    >
                      <MenuItem value="en_US">English (US)</MenuItem>
                      <MenuItem value="en_GB">English (UK)</MenuItem>
                      <MenuItem value="hi">Hindi</MenuItem>
                      <MenuItem value="es">Spanish</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Category <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Box sx={{ mb: category === 'AUTHENTICATION' ? 2.5 : 0 }}>
                    <Grid container spacing={2}>
                      {[
                        {
                          value: 'UTILITY',
                          label: 'Utility',
                          hint: 'Transactional updates',
                          icon: <Build />,
                          color: '#1976d2',
                        },
                        {
                          value: 'MARKETING',
                          label: 'Marketing',
                          hint: 'Offers and promotions',
                          icon: <Campaign />,
                          color: '#ed6c02',
                        },
                        {
                          value: 'AUTHENTICATION',
                          label: 'Authentication',
                          hint: 'OTP and verification',
                          icon: <Security />,
                          color: '#2e7d32',
                        },
                      ].map((cat) => {
                        const selected = category === cat.value;
                        return (
                          <Grid item xs={12} sm={4} key={cat.value}>
                            <Card
                              elevation={0}
                              sx={{
                                position: 'relative',
                                p: 2,
                                pr: selected ? 4 : 2,
                                cursor: 'pointer',
                                height: '100%',
                                boxSizing: 'border-box',
                                borderRadius: 2,
                                border: `2px solid ${selected ? cat.color : 'transparent'}`,
                                outline: selected ? 'none' : `1px solid ${WB_UI.color.border}`,
                                outlineOffset: selected ? 0 : '-1px',
                                backgroundColor: selected ? `${cat.color}0D` : '#ffffff',
                                transition: 'border-color 0.15s, background-color 0.15s',
                                overflow: 'hidden',
                                '&:hover': {
                                  borderColor: cat.color,
                                  outlineColor: cat.color,
                                  backgroundColor: `${cat.color}12`,
                                },
                              }}
                              onClick={() => {
                                setCategory(cat.value);
                                setCurrentStep(0);
                                if (cat.value === 'AUTHENTICATION') {
                                  setAddSecurityRecommendation(true);
                                  setOtpType('COPY_CODE');
                                }
                              }}
                            >
                              {selected && (
                                <CheckCircle
                                  sx={{
                                    position: 'absolute',
                                    top: 10,
                                    right: 10,
                                    fontSize: 18,
                                    color: cat.color,
                                  }}
                                />
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1.5,
                                    display: 'grid',
                                    placeItems: 'center',
                                    color: cat.color,
                                    backgroundColor: `${cat.color}18`,
                                    flexShrink: 0,
                                  }}
                                >
                                  {cat.icon}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 800,
                                      lineHeight: 1.25,
                                      pr: selected ? 0.5 : 0,
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {cat.label}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {cat.hint}
                                  </Typography>
                                </Box>
                              </Box>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                  {category === 'AUTHENTICATION' && (
                    <Alert
                      severity="info"
                      icon={<Security fontSize="inherit" />}
                      sx={{
                        position: 'relative',
                        zIndex: 0,
                        borderRadius: 2,
                        backgroundColor: '#e8f5e9',
                        color: '#1b5e20',
                        '& .MuiAlert-icon': { color: '#2e7d32' },
                      }}
                    >
                      Meta generates the OTP body. Do not write custom body text. Use copy-code or one-tap. When sending, pass the OTP in BODY; the backend adds the button.
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 1: Header */}
          {!isAuthTemplate && currentStep === 1 && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ mb: 0.75, ...WB_SX.sectionTitle, color: 'text.primary' }}>
                Header (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Configure the header section. You can choose text or media (image, video, document).
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Header Type (Optional)
                  </Typography>
                    <Grid container spacing={2}>
                      {[
                        {
                          value: 'TEXT',
                          label: 'Text',
                          icon: <TextFields />,
                          color: '#1976d2',
                        },
                        {
                          value: 'IMAGE',
                          label: 'Image',
                          icon: <Image />,
                          color: '#ed6c02',
                        },
                        {
                          value: 'VIDEO',
                          label: 'Video',
                          icon: <VideoFile />,
                          color: '#9c27b0',
                        },
                        {
                          value: 'DOCUMENT',
                          label: 'Document',
                          icon: <InsertDriveFile />,
                          color: '#2e7d32',
                        },
                      ].map((type) => (
                        <Grid item xs={12} sm={6} md={3} key={type.value}>
                          <Card
                            elevation={0}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              borderRadius: 2,
                              border: header.type === type.value ? `2px solid ${type.color}` : `1px solid ${WB_UI.color.border}`,
                              backgroundColor: header.type === type.value ? `${type.color}0D` : '#ffffff',
                              transition: 'border-color 0.15s, background-color 0.15s',
                              '&:hover': {
                                borderColor: type.color,
                                backgroundColor: `${type.color}12`,
                              },
                            }}
                            onClick={() => {
                              // Reset media when changing type
                              const newType = type.value;
                              let mediaPreview = null;
                              if (newType === 'IMAGE') {
                                mediaPreview = WHATSAPP_MEDIA_EXAMPLE_URLS.IMAGE;
                              } else if (newType === 'VIDEO') {
                                mediaPreview = WHATSAPP_MEDIA_EXAMPLE_URLS.VIDEO;
                              } else if (newType === 'DOCUMENT') {
                                mediaPreview = WHATSAPP_MEDIA_EXAMPLE_URLS.DOCUMENT;
                              }
                              
                              setHeader(prevHeader => ({ 
                                ...prevHeader, 
                                type: newType, 
                                text: newType === "TEXT" ? prevHeader.text : "",
                                mediaPreview: mediaPreview,
                                mediaId: null,
                                mediaSource: 'system',
                                defaultMediaLink: null,
                              }));
                              
                              // Automatically upload media when type is selected (if not TEXT)
                              // Pass newType directly to avoid stale closure issues
                              if (newType !== "TEXT") {
                                // Small delay to ensure state is updated
                                setTimeout(() => {
                                  handleMediaUpload(newType);
                                }, 100);
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1.5,
                                  display: 'grid',
                                  placeItems: 'center',
                                  color: type.color,
                                  backgroundColor: `${type.color}18`,
                                  flexShrink: 0,
                                }}
                              >
                                {type.icon}
                              </Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, flex: 1 }}>
                                {type.label}
                              </Typography>
                              {header.type === type.value && (
                                <CheckCircle sx={{ fontSize: 18, color: type.color }} />
                              )}
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                </Grid>
                {header.type === "TEXT" && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Header Text"
                        value={header.text}
                        onChange={(e) => handleContentChange(e, 'header')}
                        onSelect={(e) => handleTextSelection(e, 'header')}
                        onClick={(e) => {
                          const inputElement = e.target;
                          const cursorPos = inputElement.selectionStart ?? (inputElement.value?.length || 0);
                          setCursorPosition(prev => ({ ...prev, header: cursorPos }));
                        }}
                        onKeyUp={(e) => {
                          const inputElement = e.target;
                          const cursorPos = inputElement.selectionStart ?? (inputElement.value?.length || 0);
                          setCursorPosition(prev => ({ ...prev, header: cursorPos }));
                        }}
                        inputRef={headerFieldRef}
                        placeholder="Enter header text (max 60 characters)"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <TextFields fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        helperText={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            <span>{(header.text || '').length}/60 characters</span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Code />}
                              onClick={() => insertNextParameter("header")}
                              sx={{ minWidth: 'auto', px: 1.5, py: 0.5 }}
                            >
                              Insert Parameter
                            </Button>
                          </Box>
                        }
                      />
                </Grid>
                )}
                {/* Media Upload Status for Header (IMAGE, VIDEO, DOCUMENT) */}
                {(header.type === "IMAGE" || header.type === "VIDEO" || header.type === "DOCUMENT") && (
                  <Grid item xs={12}>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                          {header.type} Header
                        </Typography>

                        {/* Media source choice: System assigned vs Custom (default media) */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          <Chip
                            label="System assigned media"
                            onClick={() => setHeader(prev => ({
                              ...prev,
                              mediaSource: 'system',
                              mediaId: null,
                              defaultMediaLink: null,
                            }))}
                            color={header.mediaSource === 'system' ? 'primary' : 'default'}
                            variant={header.mediaSource === 'system' ? 'filled' : 'outlined'}
                            size="small"
                          />
                          <Chip
                            label="Custom (default media)"
                            onClick={() => setHeader(prev => ({
                              ...prev,
                              mediaSource: 'custom',
                              mediaId: null,
                              defaultMediaLink: null,
                              mediaPreview: getInitialMediaPreview(header.type),
                            }))}
                            color={header.mediaSource === 'custom' ? 'primary' : 'default'}
                            variant={header.mediaSource === 'custom' ? 'filled' : 'outlined'}
                            size="small"
                          />
                        </Box>

                        {header.mediaSource === 'system' && (
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                            Using file from public folder: {MEDIA_FILES[header.type]}. Media will be uploaded to WhatsApp.
                          </Typography>
                        )}
                        {header.mediaSource === 'custom' && (
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                            Upload your own file. Media will be uploaded to WhatsApp and stored in Cloudflare R2.
                          </Typography>
                        )}
                        
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                          {uploadingMedia && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <CircularProgress size={20} />
                              <Typography variant="body2" color="primary">
                                Uploading to WhatsApp...
                              </Typography>
                            </Box>
                          )}
                          
                          {!uploadingMedia && !header.mediaId && header.mediaSource === 'system' && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={handleMediaUpload}
                              startIcon={<CloudUpload />}
                            >
                              Upload to WhatsApp
                            </Button>
                          )}

                          {!uploadingMedia && !header.mediaId && header.mediaSource === 'custom' && (
                            <>
                              <input
                                ref={customMediaInputRef}
                                type="file"
                                accept={
                                  header.type === 'IMAGE' ? 'image/jpeg,image/jpg,image/png,image/gif' :
                                  header.type === 'VIDEO' ? 'video/mp4,video/mpeg' :
                                  'application/pdf,.doc,.docx'
                                }
                                style={{ display: 'none' }}
                                onChange={handleCustomMediaFileChange}
                              />
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={handleCustomMediaClick}
                                startIcon={<CloudUpload />}
                              >
                                Choose & Upload custom media
                              </Button>
                            </>
                          )}
                          
                          {!uploadingMedia && header.mediaId && (
                            <Chip
                              label={
                                header.defaultMediaLink
                                  ? `✓ Handle & default_media_link ready`
                                  : `✓ Handle ready: ${header.mediaId.substring(0, 30)}...`
                              }
                              color="success"
                              size="small"
                              title={header.mediaId}
                            />
                          )}
                        </Box>
                        
                        {/* Preview */}
                        {header.mediaPreview && (
                          <Box sx={{ mt: 2 }}>
                            {header.type === "IMAGE" && (
                              <Box
                                component="img"
                                src={header.mediaPreview}
                                alt="Header preview"
                                sx={{
                                  maxWidth: '100%',
                                  maxHeight: '200px',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0',
                                  objectFit: 'contain',
                                }}
                              />
                            )}
                            {header.type === "VIDEO" && (
                              <Box
                                component="video"
                                src={header.mediaPreview}
                                controls
                                sx={{
                                  maxWidth: '100%',
                                  maxHeight: '200px',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0',
                                }}
                              />
                            )}
                            {header.type === "DOCUMENT" && (
                              <Box sx={{ 
                                p: 2, 
                                border: '1px solid #e0e0e0', 
                                borderRadius: 1,
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <InsertDriveFile fontSize="small" color="primary" />
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {MEDIA_FILES[header.type] ? MEDIA_FILES[header.type].split('/').pop() : 'Document'}
                                </Typography>
                                {header.mediaId && (
                                  <Chip
                                    label={`Handle: ${header.mediaId.substring(0, 20)}...`}
                                    color="success"
                                    size="small"
                                    title={header.mediaId}
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Instructions */}
                        <Box sx={{ mt: 2, p: 1.5, ...WB_SX.infoBox }}>
                          <Typography variant="caption" color="text.secondary" component="div">
                            <strong>• System assigned media:</strong> Use this to create example media for template creation when you don&apos;t have media ready. It uses sample files for template approval.
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                            <strong>• Custom (default media):</strong> Select this option to upload your own media file and configure it as the default media for the template.
                            (Default media is supported for Bot only.)
                          </Typography>
                        </Box>
                      </Box>
                  </Grid>
                )}
                {/* Example Parameters for Header */}
                {header.type === "TEXT" && header.text.trim() && extractParameters(header.text).length > 0 && (
                  <Grid item xs={12}>
                      <Box sx={{ mt: 2, p: 2, ...WB_SX.infoBox }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Example Parameters for Header (Optional)
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Provide example values for template preview. These will be used when submitting the template for approval.
                        </Typography>
                        <Grid container spacing={1}>
                          {extractParameters(header.text).map((paramNum, index) => (
                            <Grid item xs={12} sm={6} key={paramNum}>
                              <TextField
                                fullWidth
                                size="small"
                                label={`Example for {{${paramNum}}}`}
                                value={header.examples[index] || ''}
                                onChange={(e) => {
                                  const newExamples = [...(header.examples || [])];
                                  newExamples[index] = e.target.value;
                                  setHeader({ ...header, examples: newExamples });
                                }}
                                placeholder={`e.g., Sample Value ${paramNum}`}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <PlayArrow fontSize="small" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Step 2: Body */}
          {!isAuthTemplate && currentStep === 2 && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ mb: 0.75, ...WB_SX.sectionTitle, color: 'text.primary' }}>
                Body (Required)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Enter the main message content. Use parameters like {'{{1}}'}, {'{{2}}'} for dynamic values. You can format text with *bold*, _italic_, or ~strikethrough~.
              </Typography>
                <TextField
                  fullWidth
                  label="Body Text"
                  value={body.text}
                  onChange={(e) => handleContentChange(e, 'body')}
                  onSelect={(e) => handleTextSelection(e, 'body')}
                  onClick={(e) => {
                    const inputElement = e.target;
                    const cursorPos = inputElement.selectionStart ?? (inputElement.value?.length || 0);
                    setCursorPosition(prev => ({ ...prev, body: cursorPos }));
                  }}
                  onKeyUp={(e) => {
                    const inputElement = e.target;
                    const cursorPos = inputElement.selectionStart ?? (inputElement.value?.length || 0);
                    setCursorPosition(prev => ({ ...prev, body: cursorPos }));
                  }}
                  inputRef={bodyFieldRef}
                  placeholder="Enter your message text. Use {{1}}, {{2}}, etc. for dynamic parameters"
                  multiline
                  rows={6}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                        <Message fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    style: { whiteSpace: 'pre-wrap' }
                  }}
                  helperText={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                      <span>{(body.text || '').length}/1024 characters</span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Code />}
                        onClick={() => insertNextParameter("body")}
                        sx={{ minWidth: 'auto', px: 1.5, py: 0.5 }}
                      >
                        Insert Parameter
                      </Button>
                    </Box>
                  }
                />
                
                {/* Formatting Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                  <ButtonGroup variant="outlined" size="small">
                    <Tooltip title="Bold (Select text first)">
                      <Button
                        onClick={() => handleFormatToggle('bold')}
                        color="primary"
                        disabled={!hasSelection || selection.field !== 'body'}
                      >
                        <FormatBold />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Italic (Select text first)">
                      <Button
                        onClick={() => handleFormatToggle('italic')}
                        color="primary"
                        disabled={!hasSelection || selection.field !== 'body'}
                      >
                        <FormatItalic />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Strikethrough (Select text first)">
                      <Button
                        onClick={() => handleFormatToggle('strikethrough')}
                        color="primary"
                        disabled={!hasSelection || selection.field !== 'body'}
                      >
                        <FormatStrikethrough />
                      </Button>
                    </Tooltip>
                  </ButtonGroup>
                </Box>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ textAlign: 'center' }}>
                  {hasSelection && selection.field === 'body'
                    ? `Formatting will be applied to selected text (${selection.end - selection.start} characters)`
                    : 'Select text in body to apply formatting: *bold*, _italic_, ~strikethrough~'}
                </Typography>

                {/* Example Parameters for Body */}
                {body.text.trim() && extractParameters(body.text).length > 0 && (
                  <Box sx={{ mt: 2, p: 2, ...WB_SX.infoBox }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Example Parameters for Body (Optional)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Provide example values for template preview. These will be used when submitting the template for approval.
                    </Typography>
                    <Grid container spacing={1}>
                      {extractParameters(body.text).map((paramNum, index) => (
                        <Grid item xs={12} sm={6} key={paramNum}>
                          <TextField
                            fullWidth
                            size="small"
                            label={`Example for {{${paramNum}}}`}
                            value={body.examples[index] || ''}
                            onChange={(e) => {
                              const newExamples = [...(body.examples || [])];
                              newExamples[index] = e.target.value;
                              setBody({ ...body, examples: newExamples });
                            }}
                            placeholder={`e.g., Sample Value ${paramNum}`}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PlayArrow fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
            </Box>
          )}

          {/* Step 3: Footer */}
          {!isAuthTemplate && currentStep === 3 && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ mb: 0.75, ...WB_SX.sectionTitle, color: 'text.primary' }}>
                Footer (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Add a footer text that will appear at the bottom of your message (max 60 characters).
              </Typography>
              <TextField
                  fullWidth
                  label="Footer Text"
                  value={footer.text}
                  onChange={(e) => handleContentChange(e, 'footer')}
                  onSelect={(e) => handleTextSelection(e, 'footer')}
                  inputRef={footerFieldRef}
                  placeholder="Enter footer text (max 60 characters)"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Subtitles fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                helperText={`${(footer.text || '').length}/60 characters`}
              />
            </Box>
          )}

          {/* Step 4: Buttons */}
          {!isAuthTemplate && currentStep === 4 && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ mb: 0.75, ...WB_SX.sectionTitle, color: 'text.primary' }}>
                Buttons (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Add up to 3 buttons to your template. URL buttons can include parameters like {'{{1}}'} in the URL.
              </Typography>
                {buttons.map((button, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, border: `1px solid ${WB_UI.color.border}`, borderRadius: 2, backgroundColor: '#fafafa' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2">Button {index + 1}</Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          const newButtons = buttons.filter((_, i) => i !== index);
                          setButtons(newButtons);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Button Type</InputLabel>
                          <Select
                            value={button.type || 'URL'}
                            onChange={(e) => {
                              const newButtons = [...buttons];
                              const newType = e.target.value;
                              newButtons[index] = {
                                ...newButtons[index],
                                type: newType,
                                text: newButtons[index].text || '',
                                url: newType === 'URL' ? (newButtons[index].url || '') : undefined,
                                phone_number: newType === 'PHONE_NUMBER' ? (newButtons[index].phone_number || '') : undefined,
                              };
                              setButtons(newButtons);
                            }}
                          >
                            <MenuItem value="URL">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Link fontSize="small" />
                                URL
                              </Box>
                            </MenuItem>
                            <MenuItem value="QUICK_REPLY">Quick Reply</MenuItem>
                            <MenuItem value="PHONE_NUMBER">Phone Number</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Button Text"
                          value={button.text || ''}
                          onChange={(e) => {
                            const newButtons = [...buttons];
                            newButtons[index] = {
                              ...newButtons[index],
                              text: e.target.value,
                            };
                            setButtons(newButtons);
                          }}
                          placeholder="e.g., View Order"
                          helperText={`${(button.text || '').length}/25 characters`}
                        />
                      </Grid>
                      {button.type === 'URL' && (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="URL"
                            value={button.url || ''}
                            onChange={(e) => {
                              const newButtons = [...buttons];
                              newButtons[index] = {
                                ...newButtons[index],
                                url: e.target.value,
                              };
                              setButtons(newButtons);
                            }}
                            placeholder="e.g., https://example.com/orders/{{1}}"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Link fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      )}
                      {button.type === 'PHONE_NUMBER' && (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Phone Number"
                            value={button.phone_number || ''}
                            onChange={(e) => {
                              const newButtons = [...buttons];
                              newButtons[index] = {
                                ...newButtons[index],
                                phone_number: e.target.value,
                              };
                              setButtons(newButtons);
                            }}
                            placeholder="e.g., 919354387864 (country code + number, no + sign)"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Phone fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                            helperText="Enter phone number with country code (e.g., 919354387864)"
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                ))}
                {buttons.length < 3 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => {
                      setButtons([...buttons, { type: 'URL', text: '', url: '' }]);
                    }}
                  >
                    Add Button
                  </Button>
                )}
                {buttons.length >= 3 && (
                  <Typography variant="caption" color="text.secondary">
                    Maximum 3 buttons allowed
                  </Typography>
                )}
            </Box>
          )}

          {/* AUTH Step 1: OTP message */}
          {isAuthTemplate && currentStep === 1 && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ mb: 0.75, ...WB_SX.sectionTitle, color: 'text.primary' }}>
                OTP message
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Meta supplies the verification-code copy. Authentication body always has one OTP parameter ({'{{1}}'}). Custom body text is not sent.
              </Typography>
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${WB_UI.color.border}`,
                  backgroundColor: '#e5ddd5',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#075e54', display: 'block', mb: 1 }}>
                  Live preview
                </Typography>
                <Box
                  sx={{
                    maxWidth: 360,
                    backgroundColor: '#ffffff',
                    borderRadius: '0 10px 10px 10px',
                    p: 1.5,
                    boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {authPreviewBody.replace('{{1}}', '123456')}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${WB_UI.color.border}`,
                  backgroundColor: addSecurityRecommendation ? '#e8f5e9' : '#fff',
                }}
              >
                <FormControlLabel
                  sx={{ m: 0, alignItems: 'flex-start' }}
                  control={
                    <Switch
                      checked={addSecurityRecommendation}
                      onChange={(e) => setAddSecurityRecommendation(e.target.checked)}
                      sx={{
                        mt: 0.25,
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#2e7d32' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2e7d32' },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Add security recommendation
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Appends “For your security, do not share this code.”
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              <TextField
                fullWidth
                type="number"
                label="Message send TTL (seconds, optional)"
                value={messageSendTtlSeconds}
                onChange={(e) => setMessageSendTtlSeconds(e.target.value)}
                placeholder="e.g. 600"
                helperText="Optional. Minimum 30 seconds. How long WhatsApp will attempt to deliver the OTP."
                inputProps={{ min: 30 }}
              />
            </Box>
          )}

          {/* AUTH Step 2: Code expiration */}
          {isAuthTemplate && currentStep === 2 && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ mb: 0.75, ...WB_SX.sectionTitle, color: 'text.primary' }}>
                Code expiration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Optional footer. Meta shows how long the code is valid. Leave empty to omit the footer.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                {[
                  { value: '', label: 'No footer' },
                  { value: '5', label: '5 min' },
                  { value: '10', label: '10 min' },
                  { value: '15', label: '15 min' },
                  { value: '30', label: '30 min' },
                ].map((preset) => {
                  const selected = String(codeExpirationMinutes || '') === preset.value;
                  return (
                    <Chip
                      key={preset.label}
                      label={preset.label}
                      onClick={() => setCodeExpirationMinutes(preset.value)}
                      variant={selected ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 700,
                        borderRadius: 2,
                        backgroundColor: selected ? '#2e7d32' : '#fff',
                        color: selected ? '#fff' : 'text.primary',
                        borderColor: selected ? '#2e7d32' : WB_UI.color.border,
                        '&:hover': {
                          backgroundColor: selected ? '#1b5e20' : '#e8f5e9',
                        },
                      }}
                    />
                  );
                })}
              </Box>
              <TextField
                fullWidth
                type="number"
                label="Code expiration (minutes)"
                value={codeExpirationMinutes}
                onChange={(e) => setCodeExpirationMinutes(e.target.value)}
                placeholder="e.g. 10"
                helperText="Optional. 1–90 minutes."
                inputProps={{ min: 1, max: 90 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTime fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          {/* AUTH Step 3: OTP button */}
          {isAuthTemplate && currentStep === 3 && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ mb: 0.75, ...WB_SX.sectionTitle, color: 'text.primary' }}>
                OTP button
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Copy-code lets the user copy the OTP. One-tap autofills it in your Android app. Both use the same send shape (`button` + `sub_type: url`).
              </Typography>
              <Grid container spacing={2}>
                {[
                  { value: 'COPY_CODE', label: 'Copy code', icon: <ContentCopy />, helper: 'User taps to copy the OTP' },
                  { value: 'ONE_TAP', label: 'One-tap', icon: <PhoneAndroid />, helper: 'Autofill in your Android app' },
                ].map((opt) => (
                  <Grid item xs={12} sm={6} key={opt.value}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2,
                        height: '100%',
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: otpType === opt.value ? '2px solid #2e7d32' : `1px solid ${WB_UI.color.border}`,
                        backgroundColor: otpType === opt.value ? '#e8f5e9' : '#fff',
                      }}
                      onClick={() => setOtpType(opt.value)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            display: 'grid',
                            placeItems: 'center',
                            color: '#2e7d32',
                            backgroundColor: '#c8e6c9',
                            flexShrink: 0,
                          }}
                        >
                          {opt.icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{opt.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{opt.helper}</Typography>
                        </Box>
                        {otpType === opt.value && <CheckCircle sx={{ color: '#2e7d32', fontSize: 18, mt: 0.25 }} />}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {otpType === 'ONE_TAP' && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${WB_UI.color.border}`,
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Alert
                    severity="info"
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      backgroundColor: '#e8f5e9',
                      color: '#1b5e20',
                      '& .MuiAlert-icon': { color: '#2e7d32' },
                    }}
                  >
                    ONE_TAP requires your Android app identifiers. Copy-code is enough if you do not have an app.
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Package name"
                        value={supportedApp.package_name}
                        onChange={(e) => setSupportedApp((s) => ({ ...s, package_name: e.target.value }))}
                        placeholder="com.example.app"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Signature hash"
                        value={supportedApp.signature_hash}
                        onChange={(e) => setSupportedApp((s) => ({ ...s, signature_hash: e.target.value }))}
                        placeholder="11-character hash"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          {/* Navigation Buttons */}
          <Box sx={{ mt: 4, pt: 2.5, borderTop: `1px solid ${WB_UI.color.border}`, display: "flex", justifyContent: "space-between", gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant='outlined'
              onClick={() => navigate('/whatsapp-business/templates')}
              size="medium"
              sx={WB_SX.secondaryButton}
            >
              Cancel
            </Button>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                disabled={currentStep === 0}
                onClick={handleBack}
                startIcon={<ChevronLeft />}
                size="medium"
                variant="outlined"
                sx={WB_SX.secondaryButton}
              >
                Back
              </Button>
              <Button
                variant='contained'
                color='primary'
                startIcon={uploadingMedia ? <CircularProgress size={16} color="inherit" /> : (currentStep === lastStep ? <Save /> : <NavigateNext />)}
                onClick={handleNext}
                size="medium"
                sx={
                  isAuthTemplate
                    ? {
                        ...WB_SX.primaryButton,
                        backgroundColor: '#2e7d32',
                        '&:hover': { backgroundColor: '#1b5e20' },
                      }
                    : WB_SX.primaryButton
                }
                disabled={
                  uploadingMedia ||
                  (currentStep === lastStep && isAuthTemplate && otpType === 'ONE_TAP' && (
                    !supportedApp.package_name.trim() || !supportedApp.signature_hash.trim()
                  )) ||
                  (currentStep === lastStep && !isAuthTemplate && (
                    !templateName.trim() ||
                    !body.text.trim() ||
                    ((header.type === "IMAGE" || header.type === "VIDEO" || header.type === "DOCUMENT") && !header.mediaId)
                  ))
                }
              >
                {uploadingMedia
                  ? 'Uploading Media...'
                  : currentStep === lastStep
                    ? (isEditMode ? 'Update' : 'Create') + ' Template'
                    : 'Next'
                }
              </Button>
            </Box>
          </Box>
        </Paper>
        </Grid>

        {/* Preview Sidebar */}
        {previewVisible && (
          <Grid item xs={12} md={5}>
            <Box sx={{ position: 'sticky', top: 20, alignSelf: 'flex-start' }}>
              <TemplatePreview
                templateName={templateName}
                language={language}
                category={category}
                header={isAuthTemplate ? { type: 'TEXT', text: '', examples: [] } : header}
                body={isAuthTemplate ? { text: authPreviewBody, examples: ['123456'] } : body}
                footer={isAuthTemplate ? { text: authPreviewFooter } : footer}
                buttons={isAuthTemplate ? authPreviewButtons : buttons}
                headerMediaPreviewUrl={isAuthTemplate ? null : header.mediaPreview}
              />
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
      >
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the template "{templateName}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleDeleteTemplate} variant="contained" color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateEditWhatsappBusinessTemplate;

